import { PenTool, Send, Trash2, Upload } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { TooltipProvider } from "@/components/ui/tooltip";

import { MattersAPI } from "../../../api/matters";
import { CLIENT_DOCUMENT_ACCEPT } from "../../../api/types/foreignNationalDocument";
import { ClientTableToolbar } from "../../clientProfile/components/table/ClientTableToolbar";
import { useFocusScroll } from "../../clientProfile/hooks/useFocusScroll";
import { useOnClientInvalidate } from "../../clientProfile/lib/clientInvalidation";
import { ClientPortalFloatingTableShell } from "../../foreignNationals/components/ClientPortalFloatingTableShell";
import {
  DEFAULT_DOCUMENTS_SORT,
  DOCUMENTS_SORT_OPTIONS,
  documentFocusDomId,
  filterDocuments,
  isSortSelected,
  sortDocuments,
  type DocumentsSort
} from "../documentsModel";
import { useForeignNationalDocuments } from "../hooks/useForeignNationalDocuments";
import type { ForeignNationalDocument } from "../types";
import { DocumentsChecklist } from "./checklist/DocumentsChecklist";
import { ForeignNationalDocumentQuickPreviewDrawer } from "./ForeignNationalDocumentQuickPreviewDrawer";
import { ForeignNationalDocumentsSendToCaseDrawer } from "./ForeignNationalDocumentsSendToCaseDrawer";
import { ForeignNationalDocumentsTable } from "./ForeignNationalDocumentsTable";
import { runUploadBatch } from "./UploadProgressToast";

type DocumentsTableShell = "embedded" | "floating";

type Props = {
  foreignNationalId: string;
  /**
   * The section title. Given, the panel heads itself (refresh, Upload, New
   * Request), then the case files, then the library with its search and sort.
   */
  title?: string;
  /** Show the "Referenced in" column in the documents table. */
  showReferencedInColumn?: boolean;
  /** Adjust UI for the portal (foreign national) view. */
  variant?: "law_firm" | "foreign_national";
  /** Controls row selection + bulk actions UI. */
  showSelection?: boolean;
  /** Floating card on dotted background (client management portal). */
  tableShell?: DocumentsTableShell;
  /**
   * Starts a new outgoing request ("New Request"; Ask for Rescan on the
   * firm's checklist passes the documents template). Hidden if omitted.
   */
  onCreateRequest?: (opts?: { templateHint?: string }) => void;
  /** Firm view: the client's first name, for "Uploaded by Priya". */
  clientFirstName?: string | null;
  /** `?documentId=` deep link: scroll to and highlight this document's row. */
  focusDocumentId?: string | null;
  /** Firm view: "Ask to sign" when one PDF is selected. */
  onRequestSignature?: {
    label: string;
    onRequest: (doc: ForeignNationalDocument) => void;
  };
};

type LibraryProps = Omit<Props, "clientFirstName">;

const isPdf = (doc: ForeignNationalDocument): boolean =>
  /pdf/i.test(doc.content_type ?? "") || /\.pdf$/i.test(doc.filename);

/**
 * A foreign national's documents: the firm sees the checklist by type
 * (client documents and case files merged); the client portal keeps the
 * library table.
 */
export function ForeignNationalDocumentsPanel({
  clientFirstName,
  ...props
}: Props) {
  const {
    variant = "law_firm",
    foreignNationalId,
    focusDocumentId,
    onCreateRequest,
    onRequestSignature
  } = props;
  if (variant === "law_firm") {
    return (
      <DocumentsChecklist
        foreignNationalId={foreignNationalId}
        clientFirstName={clientFirstName}
        focusDocumentId={focusDocumentId}
        onCreateRequest={(opts) => onCreateRequest?.(opts)}
        onRequestSignature={onRequestSignature?.onRequest}
      />
    );
  }
  return <DocumentsLibrary {...props} />;
}

/** The library table (client portal). */
function DocumentsLibrary({
  foreignNationalId,
  title,
  showReferencedInColumn = true,
  variant = "law_firm",
  showSelection = true,
  tableShell = "embedded",
  onCreateRequest,
  focusDocumentId = null,
  onRequestSignature
}: LibraryProps) {
  const isForeignNationalView = variant === "foreign_national";
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [previewDoc, setPreviewDoc] = useState<ForeignNationalDocument | null>(
    null
  );
  const [sendToCaseOpen, setSendToCaseOpen] = useState(false);
  const [caseLabelById, setCaseLabelById] = useState<Record<string, string>>(
    {}
  );
  const [caseVisaById, setCaseVisaById] = useState<Record<string, string>>({});

  const {
    documents,
    isLoading,
    errorMessage,
    upload,
    remove,
    refresh,
    setShared
  } = useForeignNationalDocuments(foreignNationalId);
  const sharing = useMemo(
    () =>
      isForeignNationalView
        ? undefined
        : {
            clientId: foreignNationalId,
            onToggle: (doc: ForeignNationalDocument, shared: boolean) => {
              setShared(doc.foreign_national_document_id, shared)
                .then(() =>
                  toast.success(
                    shared
                      ? `${doc.filename} is now visible in the client portal.`
                      : `${doc.filename} is hidden from the client.`
                  )
                )
                .catch((e: unknown) =>
                  toast.error("Couldn't change sharing.", {
                    description: e instanceof Error ? e.message : undefined
                  })
                );
            }
          },
    [foreignNationalId, isForeignNationalView, setShared]
  );

  // The client manager agent changed the vault (status, send-to-case,
  // delete): pick up the new list right away.
  useOnClientInvalidate(foreignNationalId, ["documents"], () => {
    refresh({ silent: true }).catch(() => undefined);
  });

  useEffect(() => {
    if (!foreignNationalId) return;
    MattersAPI.listForForeignNational(foreignNationalId)
      .then((items) => {
        const labels: Record<string, string> = {};
        const visas: Record<string, string> = {};
        (items || []).forEach((m) => {
          const caseId = String(m.case_id ?? "").trim();
          if (!caseId) return;
          const visa = String(m.case?.visa ?? "").trim();
          const status = String(m.case?.status ?? "").trim();
          labels[caseId] = status
            ? `${visa || "Case"} • ${status}`
            : visa || "Case";
          if (visa) visas[caseId] = visa;
        });
        setCaseLabelById(labels);
        setCaseVisaById(visas);
      })
      .catch(() => {
        setCaseLabelById({});
        setCaseVisaById({});
      });
  }, [foreignNationalId]);

  const [sort, setSort] = useState<DocumentsSort>(DEFAULT_DOCUMENTS_SORT);
  const [query, setQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const visible = useMemo(
    () => sortDocuments(filterDocuments(documents, query), sort),
    [documents, query, sort]
  );

  // `?documentId=` (an agent's document ref): scroll to the row once loaded.
  const { highlightedId } = useFocusScroll(
    focusDocumentId ? documentFocusDomId(focusDocumentId) : null,
    !isLoading && documents.length > 0
  );
  const highlightedDocumentId =
    focusDocumentId && highlightedId === documentFocusDomId(focusDocumentId)
      ? focusDocumentId
      : null;

  // Drop selections for documents that no longer exist.
  useEffect(() => {
    const ids = new Set(documents.map((d) => d.foreign_national_document_id));
    setSelectedIds((prev) => {
      const next = new Set<string>();
      prev.forEach((id) => {
        if (ids.has(id)) next.add(id);
      });
      return next.size === prev.size ? prev : next;
    });
  }, [documents]);

  const anySelected = showSelection && selectedIds.size > 0;
  const selectedDocs = useMemo(
    () =>
      documents.filter((d) => selectedIds.has(d.foreign_national_document_id)),
    [documents, selectedIds]
  );
  const allVisibleSelected =
    visible.length > 0 &&
    visible.every((d) => selectedIds.has(d.foreign_national_document_id));

  const toggleSelected = useCallback((docId: string, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) next.add(docId);
      else next.delete(docId);
      return next;
    });
  }, []);

  const toggleSelectAllVisible = useCallback(
    (selected: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        visible.forEach((d) => {
          if (selected) next.add(d.foreign_national_document_id);
          else next.delete(d.foreign_national_document_id);
        });
        return next;
      });
    },
    [visible]
  );

  const bulkDelete = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setIsBulkDeleting(true);
    const toastId = toast.loading("Deleting documents", {
      description: `${ids.length} selected`
    });
    const results = await Promise.allSettled(ids.map((id) => remove(id)));
    const failures = results.filter((r) => r.status === "rejected").length;
    const successes = ids.length - failures;
    setSelectedIds(new Set());
    setIsBulkDeleting(false);
    if (failures > 0) {
      toast.error("Some deletes failed", {
        id: toastId,
        description: `${successes} deleted, ${failures} failed`
      });
    } else {
      toast.success("Documents deleted", {
        id: toastId,
        description: `${successes} deleted`
      });
    }
  }, [remove, selectedIds]);

  const lastErrorRef = useRef<string | null>(null);
  useEffect(() => {
    if (!errorMessage?.trim() || lastErrorRef.current === errorMessage) return;
    lastErrorRef.current = errorMessage;
    toast.error("Documents", { description: errorMessage });
  }, [errorMessage]);

  const onFilesPicked = (files: File[]) => {
    if (files.length === 0) return;
    runUploadBatch(files, upload).catch(() => undefined);
  };
  const openFilePicker = () => fileInputRef.current?.click();

  const sortKey =
    DOCUMENTS_SORT_OPTIONS.find((o) => isSortSelected(o.sort, sort))?.key ??
    "default";

  const search = {
    value: query,
    onChange: setQuery,
    placeholder: "Search documents...",
    ariaLabel: "Search documents"
  };
  const sortConfig = {
    ariaLabel: "Sort documents",
    options: DOCUMENTS_SORT_OPTIONS.map((o) => ({
      value: o.key,
      label: o.label
    })),
    value: sortKey,
    onChange: (key: string) => {
      const next = DOCUMENTS_SORT_OPTIONS.find((o) => o.key === key);
      if (next) setSort(next.sort);
    }
  };

  const toolbar = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        multiple
        accept={CLIENT_DOCUMENT_ACCEPT}
        onChange={(e) => {
          onFilesPicked(Array.from(e.target.files ?? []));
          e.target.value = "";
        }}
      />
      <ClientTableToolbar
        title={title}
        search={title ? undefined : search}
        sort={title ? undefined : sortConfig}
        filters={
          anySelected ? (
            <span className="text-base whitespace-nowrap text-fg-3">
              {selectedIds.size} selected
            </span>
          ) : null
        }
        onRefresh={() => refresh().catch(() => undefined)}
        isRefreshing={isLoading}
        secondaryActions={
          anySelected ? (
            <>
              {onRequestSignature &&
              selectedDocs.length === 1 &&
              isPdf(selectedDocs[0]) ? (
                <Button
                  variant="secondary"
                  disabled={isBulkDeleting}
                  onClick={() => onRequestSignature.onRequest(selectedDocs[0])}
                  data-testid="documents-ask-to-sign"
                >
                  <PenTool />
                  {onRequestSignature.label}
                </Button>
              ) : null}
              <Button
                variant="primary"
                disabled={isBulkDeleting}
                onClick={() => setSendToCaseOpen(true)}
              >
                <Send />
                Send to Case
              </Button>
              <Button
                variant="danger"
                disabled={isBulkDeleting}
                onClick={() => bulkDelete().catch(() => undefined)}
              >
                {isBulkDeleting ? <Spinner className="size-3.5" /> : <Trash2 />}
                Delete
              </Button>
              <Button
                variant="ghost"
                disabled={isBulkDeleting}
                onClick={() => setSelectedIds(new Set())}
              >
                Cancel
              </Button>
            </>
          ) : onCreateRequest ? (
            <>
              <Button
                className="md:hidden"
                variant="secondary"
                size="icon-sm"
                aria-label="Upload"
                onClick={openFilePicker}
              >
                <Upload />
              </Button>
              <Button
                className="hidden md:inline-flex"
                variant="secondary"
                onClick={openFilePicker}
              >
                <Upload />
                Upload
              </Button>
            </>
          ) : null
        }
        primaryAction={
          anySelected
            ? undefined
            : onCreateRequest
            ? {
                label: "New Request",
                icon: "FeatherSend",
                onClick: () => onCreateRequest()
              }
            : {
                label: "Upload",
                icon: "FeatherUpload",
                onClick: openFilePicker
              }
        }
      />
    </>
  );

  const body = (
    <TooltipProvider delayDuration={150}>
      {title ? (
        <>
          {toolbar}
          <ClientTableToolbar
            title="Client Library"
            search={search}
            sort={sortConfig}
          />
        </>
      ) : (
        toolbar
      )}
      <ForeignNationalDocumentsTable
        documents={visible}
        isLoading={isLoading}
        error={documents.length === 0 ? errorMessage : null}
        onRetry={() => refresh().catch(() => undefined)}
        isFiltered={query.trim().length > 0}
        onUpload={openFilePicker}
        emptyMessage={
          isForeignNationalView
            ? "Upload a file to share it with your legal team."
            : "Upload a file to start this client's document library."
        }
        selectedIds={selectedIds}
        allVisibleSelected={allVisibleSelected}
        onToggleSelected={toggleSelected}
        onToggleSelectAll={toggleSelectAllVisible}
        onPreview={setPreviewDoc}
        caseLabelById={caseLabelById}
        caseVisaById={caseVisaById}
        showReferencedInColumn={showReferencedInColumn}
        highlightedDocumentId={highlightedDocumentId}
        showSelection={showSelection}
        sharing={sharing}
      />

      <ForeignNationalDocumentQuickPreviewDrawer
        open={Boolean(previewDoc)}
        foreignNationalId={foreignNationalId}
        document={previewDoc}
        editable={!isForeignNationalView}
        onDocumentChange={(updated) => {
          setPreviewDoc(updated);
          refresh({ silent: true }).catch(() => undefined);
        }}
        onOpenChange={(next) => {
          if (!next) setPreviewDoc(null);
        }}
      />

      <ForeignNationalDocumentsSendToCaseDrawer
        open={sendToCaseOpen}
        foreignNationalId={foreignNationalId}
        documents={selectedDocs}
        onOpenChange={setSendToCaseOpen}
        onSuccess={() => {
          setSelectedIds(new Set());
          refresh().catch(() => undefined);
        }}
      />
    </TooltipProvider>
  );

  if (tableShell === "floating") {
    return (
      <ClientPortalFloatingTableShell>
        <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
          {body}
        </div>
      </ClientPortalFloatingTableShell>
    );
  }

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-surface">
      {body}
    </div>
  );
}
